import type {
  Modifier,
  ConditionDataAttackType,
  ConditionDataProfileRole,
} from "~/types"

interface ProcessedModifier extends Modifier {
  attackType?: ConditionDataAttackType["attackType"]
  profileRole?: ConditionDataProfileRole["profileRole"]
}

/**
 * Processes profile abilities to surface attackType and profileRole conditions at the root level.
 * 
 * This function takes an array of modifiers (abilities) and extracts specific condition data
 * (attackType and profileRole) from nested condition objects, making them easily accessible
 * as top-level properties on the returned objects. This flattening improves performance
 * and simplifies access patterns in UI components.
 *
 * @param abilities - Array of modifier objects representing profile abilities
 * @returns Array of processed modifiers with attackType and profileRole surfaced as root properties
 * 
 * @example
 * ```typescript
 * const abilities = [
 *   {
 *     id: "ability1",
 *     conditions: [
 *       { type: "attackType", data: { attackType: "Melee" } },
 *       { type: "profileRole", data: { profileRole: "Attacker" } }
 *     ]
 *   }
 * ];
 * 
 * const processed = profileAbilitiesProcessed(abilities);
 * // processed[0].attackType === "Melee"
 * // processed[0].profileRole === "Attacker"
 * ```
 */
function profileAbilitiesProcessed(abilities: Modifier[]): ProcessedModifier[] {
  return abilities.map((ability) => {
    const processedAbility: ProcessedModifier = { ...ability }

    if (typeof ability.conditions !== "undefined") {
      if (ability.conditions.length) {
        ability.conditions.forEach((condition) => {
          if (condition.type === "attackType") {
            processedAbility.attackType = (
              condition.data as ConditionDataAttackType
            ).attackType
          }
          if (condition.type === "profileRole") {
            processedAbility.profileRole = (
              condition.data as ConditionDataProfileRole
            ).profileRole
          }
        })
      }
    }
    return processedAbility
  })
}

export { profileAbilitiesProcessed }
